import {
  FaroIconButton,
  FaroMenu,
  FaroMenuProps,
  grey,
  ThreeDotsIcon,
} from "@faro-lotv/flat-ui";
import {
  CSSProperties,
  forwardRef,
  JSX,
  PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

interface ScanTreeContextMenuBaseProps {
  /**
   * Whether the context-menu icon should be visible
   *
   * @default true
   */
  visible?: boolean;

  /**
   * Whether the context-menu icon should be disabled
   *
   * @default false
   */
  disabled?: boolean;

  /** Callback executed when the menu is opened/closed */
  onToggle?(open: boolean): void;

  /** Callback executed when the context menu is closed */
  onClose?(): void;

  /**
   * Enable the dark version of the menu
   *
   * @default false
   */
  dark?: boolean;

  /** Optional color for mouse hover of the ellipsis icon used to open the menu; undefined to use the color inherited from parent */
  iconHoverColor?: CSSProperties["color"];
}

/** Properties for the menu backdrop that prevent a click to be propagated to elements below */
export const slotProps: FaroMenuProps["slotProps"] = {
  backdrop: {
    onClick: (e) => e.stopPropagation(),
  },
};

export interface ScanTreeContextMenuBaseHandle {
  /** Closes the context menu when called */
  closeContextMenu(): void;
}

export const ScanTreeContextMenuBase = forwardRef<
  ScanTreeContextMenuBaseHandle,
  PropsWithChildren<ScanTreeContextMenuBaseProps>
>(function ScanTreeContextMenuBase(
  {
    visible = true,
    disabled,
    onToggle,
    onClose,
    children,
    dark = false,
    iconHoverColor,
  },
  ref,
): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null);

  const open = !!anchorEl;

  useEffect(() => {
    onToggle?.(open);
  }, [onToggle, open]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = useCallback(() => {
    // Doing this will move out the focus from the menu item before the menu becomes aria-hidden
    // This prevents assistive technology from seeing a focused element inside hidden content.
    triggerButtonRef.current?.focus();
    setAnchorEl(null);
    onClose?.();
  }, [onClose]);

  useImperativeHandle(ref, () => ({
    closeContextMenu: handleClose,
  }));

  const iconColor = dark ? grey[100] : "inherit";
  return (
    <>
      <FaroIconButton
        ref={triggerButtonRef}
        aria-label="expand sub-menu"
        onClick={handleClick}
        size="xs"
        margin={0.5}
        // Get both the image color and its hover color from the container
        color={iconColor}
        hoverColor={iconHoverColor ?? "inherit"}
        sx={{
          color: iconColor,
          visibility: visible ? "visible" : "hidden",

          // Always show for touch devices
          "@media (hover: none)": {
            visibility: "visible",
          },
        }}
        disabled={disabled}
      >
        <ThreeDotsIcon />
      </FaroIconButton>

      <FaroMenu
        aria-label="scan tree context menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        /**
         * Opening the menu triggers a scroll-lock compensation on the page body (about 8px),
         * so right-aligned UI elements get shifted left (in Chrome) without this property.
         */
        disableScrollLock
        slotProps={slotProps}
        onClick={(e) => e.stopPropagation()}
        /**
         * By default, MUI Menu uses a FocusTrap that continuously enforces focus to remain
         * within the menu. But if the menu contains editable text inputs, it can prevent text selection.
         */
        disableEnforceFocus
      >
        {children}
      </FaroMenu>
    </>
  );
});
